import { memo } from 'react'
import { ButtonBase, SxProps } from '@mui/material'
import clsx from 'clsx'
import { Text } from '@/components/Data'
import { Box } from '@/components/Layout'

import { dateRangePickerDayClasses } from './DateRangePickerDay.data'
import { DateRangePickerDayProps } from './DateRangePickerDay.types'
import { dateRangePickerDayStyles as styles } from './DateRangePickerDay.style'
import { isDateInHover, isDateInRange } from '../DateRangePicker.utils'

function DateRangePickerDay({
  sx,
  date,
  isCurrentMonth,
  startDate,
  endDate,
  hoverDate,
  labels = [],
  showDot = false,
  disabled = false,
  disableRangeHighlight = false,
  textProps = {},
  dotProps = {},
  ...props
}: DateRangePickerDayProps) {
  const isSelectedStart = !!startDate && date.isSame(startDate, 'day')
  const isSelectedEnd = !!endDate && date.isSame(endDate, 'day')
  const inSelected = isDateInRange(date, startDate, endDate)
  const inHover = !endDate && isDateInHover(date, startDate, hoverDate)
  const isBeforeStart = !!startDate && date.isBefore(startDate, 'day')
  const isAfterStart = !!startDate && date.isAfter(startDate, 'day')
  const isStartSelectedAndAfterHover =
    isSelectedStart && !!hoverDate && date.isAfter(hoverDate, 'day')
  const startAndEndSelected = isSelectedStart && !!startDate && !!endDate

  // props extraction
  const { sx: textSx, ...otherTextProps } = textProps
  const { sx: dotSx, ...otherDotProps } = dotProps

  return (
    <ButtonBase
      sx={[styles.button, sx] as SxProps}
      className={clsx({
        [dateRangePickerDayClasses.disabled]: disabled,
        [dateRangePickerDayClasses.otherMonth]: !isCurrentMonth,
        [dateRangePickerDayClasses.startDateSelected]: isSelectedStart,
        [dateRangePickerDayClasses.endDateSelected]: isSelectedEnd,
        [dateRangePickerDayClasses.highlighted]: !disableRangeHighlight && (inHover || inSelected),
        [dateRangePickerDayClasses.beforeStart]: isBeforeStart,
        [dateRangePickerDayClasses.afterStart]: isAfterStart,
        [dateRangePickerDayClasses.startDateSelectedAndAfterHover]: isStartSelectedAndAfterHover,
        [dateRangePickerDayClasses.hasValue]: startAndEndSelected,
      })}
      disabled={disabled}
      {...props}>
      <Text sx={[styles.dayText, textSx] as SxProps} {...otherTextProps}>
        {date.date()}
      </Text>

      {labels.map((label) => (
        <Text
          key={label.date.toISOString()}
          sx={styles.labelText}
          component={'span'}
          className={clsx({
            [dateRangePickerDayClasses.labelTop]: label.position === 'top',
            [dateRangePickerDayClasses.labelBottom]: label.position === 'bottom',
          })}>
          {label.label}
        </Text>
      ))}

      {showDot && <Box sx={[styles.dot, dotSx] as SxProps} {...otherDotProps} />}
    </ButtonBase>
  )
}

export default memo(DateRangePickerDay)
